import MiniFormDialog from '../../../../../components/modal/miniFormDialog';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {getFormCustomTitle} from '../../../../../util/patientContextUtil';
import clsx from 'clsx';
import {getTableScrollbar} from '../../../../../util/scrollbar';
import React, {Fragment, useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import moment from 'moment/moment';
import {getConfig} from '../../../../../util/configUtil';
import AgyleButton from '../../../../../components/ui/buttons/agyleButton';
import {ctxIdFormToLoad, setEMedLoad, setFullFormOpen, setLoadId} from '../../../../../store/reducers/patientContext';
import MessageBox from '../../../../../util/messageBox';
import useOpenState from '../../../../../hooks/useOpenState';
import {user} from '../../../../../store/reducers/user';
import Formatter from '../../../../../util/formatter';
import API from '../../../../../app/api';
import {updatePatientData} from '../../../../../store/reducers/ward';
import {addNoteForPatient} from '../../../../../store/reducers/notes';
import {setSnackbar} from '../../../../../store/reducers/globals';
import {
    additionalData,
    insertNewAlert,
    loadPatientHistoricalData,
    patientHistoryData,
    updatePatientAdditionalData,
} from '../../../../../store/reducers/additionalData';
import Notes from '../../../../../util/notes';
import AddIcon from '@mui/icons-material/Add';
import ActivitySpellSelector from '../../../../../components/notes/component/activitySpellSelector';
import MultiEntriesActivityBox from './multiEntriesActivityBox';
import {useMultiStyles} from './multiEntriesStyle';
import {SEPSIS_TOOL_TYPE} from '../../../../ward/forms/formsUtil';

const MultiEntriesFormContainer = ({
                                       open,
                                       onClose,
                                       form,
                                       patient,
                                       patientType,
                                       patientAgeType,
                                       disableSave,
                                       onLoadHistory,
                                       _onForceClose,
                                       _customOnSave,
                                   }) => {
    const classes = useMultiStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // User object
    const userObject = useSelector(user);
    const userFullName = Formatter.formatUserFullName(userObject);
    const ptHistData = useSelector(patientHistoryData);
    const loadForm = useSelector(ctxIdFormToLoad);
    const patientAddData = useSelector(additionalData);

    // Configs
    const defaultDateTimeFormat = getConfig('defaultDateTimeFormat');
    const clinicalFrailtyScoreAlertAfter6 = getConfig('clinicalFrailtyScoreAlertAfter6');

    const [selectedForm, setSelectedForm] = useState(null);

    // Open states
    const [isConfirmDeleteOpen, openConfirmDelete, closeConfirmDelete] = useOpenState(false, false);
    const [isConfirmRePrintOpen, openConfirmRePrint, closeConfirmRePrint] = useOpenState(false, false);

    // Activity selector
    const [selectedAttendance, setSelectedAttendance] = useState(null);

    // Other
    let previousPiw = null;

    const activities = useMemo(() => {
        const history = _.cloneDeep(ptHistData || []);
        if (!patient?.__id) return history;

        const index = _.findIndex(history, {__id: patient.__id});
        if (index !== -1) {
            history[index] = patient;
            return history;
        }
        return [patient, ...history];
    }, [ptHistData, patient]);

    useEffect(() => {
        if (selectedForm && loadForm) {
            dispatch(setLoadId(null));
            handleLoadSelected();
        }
    }, [selectedForm, loadForm]);

    const handleCreateNewForm = () => {
        setSelectedForm(null);
        dispatch(setFullFormOpen(true));
        onClose(false);
    };

    const handleLoadSelected = () => {
        setSelectedForm(null);
        onLoadHistory(selectedForm);
        if (selectedForm && form?.id === 'fitnessForWork') {
            dispatch(setEMedLoad(true));
        }
        dispatch(setFullFormOpen(true));
        onClose(false);
    };

    const handleCancelSelectedForm = () => {
        closeConfirmDelete();
        const isAddData = form?.options?.dataSaveDestination === 'patientData';
        const cancelObj = {
            ...selectedForm,
            canceled: true,
        };
        let id = cancelObj.id ?? cancelObj.__id;

        if (form?.id === 'sepsisTool') {
            cancelObj.status = SEPSIS_TOOL_TYPE.CANCELED;
        }

        const dataSet = _.get((isAddData ? patientAddData?.other : patient), form.options.dataPath);
        let array = _.cloneDeep(isAddData ? dataSet : dataSet?.list) || [];

        const index = _.findIndex(array, ref => ref.id === id || ref.__id === id);
        index !== -1 ? array.splice(index, 1, cancelObj) : array.push(cancelObj);

        let note = `# ${getFormCustomTitle(form, patientType, patientAgeType)}\n\n`;
        note += `* Entry at ${moment(selectedForm.date, defaultDateTimeFormat).isValid()
            ? selectedForm.date : moment(selectedForm.date).format(defaultDateTimeFormat)} has been cancelled`;

        const data = isAddData
            ? {
                [form.options.dataPath]: array,
            } : [{
                [form.options.dataPath]: {
                    list: [...array],
                    by: userFullName,
                    login: userObject?.userName,
                    date: moment().toISOString(),
                },
            }];

        id = encodeURIComponent(id);

        API.cancelClinicalNoteByRefId(patient, id)
            .then(() => {
                if (isAddData) dispatch(updatePatientAdditionalData(patient?.patient?.__id, data));
                else dispatch(updatePatientData(patient, data));

                dispatch(addNoteForPatient(patient.patient.__id, {
                    note: note,
                    noteType: form.options.dataPath,
                    patientsInWardsRef: [patient.__id],
                    episodeId: patient?.admission?.consultantEpisodeId || null,
                }, patient.__id));
                setSelectedForm(null);
                dispatch(setSnackbar({type: 'success', message: 'Form canceled successfully.'}));

                if (form.options.dataPath === 'studies.rockwood' && clinicalFrailtyScoreAlertAfter6) {
                    const filter = _.filter(array, l => !l.canceled);
                    if (!_.isEmpty(filter) && (Number(filter?.[0]?.scale?.__code) >= 6)) {
                        dispatch(insertNewAlert('cfsOver6', patient?.__id, patient?.patient?.__id, userObject, patient?.admission?.consultantEpisodeId));
                    } else {
                        dispatch(insertNewAlert('cancelCfsOver6', patient?.__id, patient?.patient?.__id, userObject, patient?.admission?.consultantEpisodeId));
                    }
                }
            })
            .catch(e => {
                console.error(e);
                dispatch(setSnackbar({type: 'error', message: 'Form was not successfully canceled.'}));
            })
            .finally(() => {
                dispatch(loadPatientHistoricalData(patient));
            });

        if (_onForceClose) _onForceClose();
    };

    const handleReprint = () => {
        closeConfirmRePrint();
        if (selectedForm) {
            let clone = _.cloneDeep(selectedForm);
            let formFormPt = _.cloneDeep(_.get(patient, form.options.dataPath));
            const index = _.findIndex(formFormPt?.list, {id: clone.id});

            clone.values.lastPrintBy = userObject?.userName;
            clone.values.lastPrintDate = moment().toISOString();
            clone.values.printCount += 1;

            if (index !== -1) {
                formFormPt.list[index] = clone;

                let data = [
                    {
                        name: form.options.dataPath,
                        value: formFormPt,
                        noteToAdd:
                            form.id === 'prescriptionExternal'
                                ? Notes.getRePrintPrescriptionExternalNote(clone)
                                : Notes.getRePrintPrescriptionNote(clone),
                        idToTrack: clone.id,
                        rePrint: true,
                    },
                ];
                dispatch(setSnackbar({type: 'info', message: t('Printing Prescription')}));
                _customOnSave(data);
                setSelectedForm(null);
            }
        }
    };

    const getAdditionalButtons = () => {
        const buttons = [];

        if (!disableSave) {
            if (selectedForm) {
                buttons.push(
                    <AgyleButton
                        key="cancel-form"
                        variant="contained"
                        disabled={selectedForm?.canceled}
                        onClick={openConfirmDelete}
                        colour="error"
                    >
                        {t('Cancel')}
                    </AgyleButton>,
                    <AgyleButton
                        key="load-form"
                        style={{marginLeft: 10}}
                        onClick={handleLoadSelected}
                        variant="contained"
                    >
                        {t('Preview')}
                    </AgyleButton>,
                );

                if (selectedForm && form?.id === 'fitnessForWork') {
                    buttons.push(
                        <AgyleButton
                            key="extend-form"
                            style={{marginLeft: 10}}
                            disabled={selectedForm?.canceled}
                            variant="contained"
                        >
                            {t('Extend')}
                        </AgyleButton>,
                    );
                }

                if (selectedForm && form?.id === 'prescription') {
                    buttons.push(
                        <AgyleButton
                            variant="contained"
                            disabled={selectedForm?.canceled}
                            onClick={openConfirmRePrint}
                        >
                            {t('Print Prescription')}
                        </AgyleButton>,
                    );
                }

                if (selectedForm && form?.id === 'prescriptionExternal') {
                    buttons.push(
                        <AgyleButton
                            variant="contained"
                            disabled={selectedForm?.canceled}
                            onClick={openConfirmRePrint}
                        >
                            {t('Print Prescription')}
                        </AgyleButton>,
                    );
                }
            }
            buttons.push(
                <AgyleButton
                    key="new-form"
                    variant="contained"
                    onClick={handleCreateNewForm}
                    startIcon={<AddIcon fontSize="small"/>}
                >
                    {t('Create new')}
                </AgyleButton>,
            );
        }
        return buttons;
    };

    const getFilteredActivity = (activities = []) => {
        if (selectedAttendance) {
            return _.filter(activities, (activity) => activity?.admission?.__id === selectedAttendance?.__id);
        }
        return activities;
    };

    return (
        <>
            <MiniFormDialog
                title={getFormCustomTitle(form, patientType, patientAgeType)}
                open={open}
                onClose={onClose}
                disableSave
                buttons={getAdditionalButtons()}
                width="sm"
                disableActionsCloseButton
                style={{opacity: loadForm ? 0 : 1}}
            >
                <>
                    <div className={clsx(getTableScrollbar(), classes.root)}>
                        <ActivitySpellSelector
                            type="activity"
                            admissions={activities?.map(n => n.admission)}
                            setCurrentActivity={setSelectedAttendance}
                        />
                        {getFilteredActivity(activities).map(piw => {
                            const currentPiw = (
                                <Fragment key={piw?.__id}>
                                    <MultiEntriesActivityBox
                                        form={form}
                                        patient={piw}
                                        onSelectForm={setSelectedForm}
                                        selectedForm={selectedForm}
                                        previousPatientActivity={previousPiw}
                                    />
                                </Fragment>
                            );
                            previousPiw = piw;
                            return currentPiw;
                        })}
                    </div>
                </>
            </MiniFormDialog>

            <MessageBox
                onClose={closeConfirmDelete}
                title={t('Confirmation')}
                message={t('Are you sure you want to cancel this entry?')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleCancelSelectedForm}
                open={isConfirmDeleteOpen}
                warning
            />

            <MessageBox
                open={isConfirmRePrintOpen}
                onClose={closeConfirmRePrint}
                title={t('Confirmation')}
                message={t('Are you sure you want to print this prescription? This Action will be recorded.')}
                haveAccept={{yes: t('Yes'), no: t('No')}}
                onAccept={handleReprint}
            />
        </>
    );
};

export default MultiEntriesFormContainer;
